import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, UserRound } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, DateTimePicker, Field, Input, Select, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';

const EMPTY_FORM = {
  assignedTo: '',
  title: '',
  description: '',
  type: 'general',
  priority: 'normal',
  dueAt: '',
};

export function AssignTaskPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), '/tasks');
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const users = useQuery({
    queryKey: ['users-for-tasks'],
    queryFn: async () => (await get<{ data: { id: string; name: string }[] }>('/org/users?limit=200')).data,
  });

  const create = useMutation({
    mutationFn: () =>
      post('/tasks', {
        assignedTo: form.assignedTo || null,
        title: form.title,
        description: form.description || undefined,
        type: form.type,
        priority: form.priority,
        dueAt: form.dueAt ? new Date(form.dueAt).toISOString() : null,
      }),
    onSuccess: () => {
      toast.success('Task assigned');
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const valid = form.title.trim().length >= 3;
  const dirty = !create.isSuccess && JSON.stringify(form) !== JSON.stringify(EMPTY_FORM);

  return (
    <FormScreen
      title="Assign a task"
      icon={ClipboardList}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        setError(null);
        create.mutate();
      }}
    >
      <FormSection title={tr('opd', 'details')} icon={ClipboardList}>
        <Field label="Task" required className="sm:col-span-2">
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Shift patient to ward 3" />
        </Field>
        <Field label="Type">
          <Select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            {['general', 'transport', 'sample_collection', 'housekeeping', 'maintenance', 'follow_up'].map((t) => (
              <option key={t} value={t}>
                {humanize(t, 'task_type')}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Priority">
          <ChoiceButtons
            layout="chips"
            value={form.priority}
            onChange={(priority) => setForm({ ...form, priority })}
            options={['low', 'normal', 'high', 'urgent'].map((p) => ({ value: p, label: humanize(p, 'priority') }))}
          />
        </Field>
        <Field label="Due">
          <DateTimePicker value={form.dueAt} onChange={(value) => setForm({ ...form, dueAt: value })} />
        </Field>
        <Field label="Detail" className="sm:col-span-2">
          <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
      </FormSection>

      <FormSection title={tr('admin', 'person')} icon={UserRound} columns={1}>
        <Field label="Assign to">
          <Select value={form.assignedTo} onChange={(e) => setForm({ ...form, assignedTo: e.target.value })}>
            <option value="">Unassigned</option>
            {users.data?.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </Select>
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Assign" loading={create.isPending} incomplete={!valid} cancelTo={returnTo} />
    </FormScreen>
  );
}
